import {Network, NetworkData} from "./network";
import {State} from "./state";
import {Register, RegisterData, RegisterValue} from "./register";
import {ActionFactory} from "./actionFactory";
import { TimedTriggerHandler, TimeoutTimedTriggerHandler } from "./timedTriggerHandler";
import {IDebugController} from "./debugController";
import {testExpression} from "./expression";

export class ErrorRegisterNotFound extends Error {
    constructor(register: string) {
        super(`Failed to find a register named ${register}`);
    }
}
export class ErrorMissingInitialState extends Error {
    constructor(initial: string) {
        super(`Failed to find initial state with key ${initial}`);
    }
}

export class Cursor {
    private actionFactory: ActionFactory;
    private controller?: IDebugController;
    private currentlyPaused: boolean = true;
    private currentlyStepping: boolean = false;
    private currentState!: State;
    private currentStateKey!: string;
    private network: Network;
    private pendingActions: boolean = true;
    private timedTriggerHandler: TimedTriggerHandler;

    private readonly currentRegisters: Map<string, Register> = new Map();
    private readonly updateDelegates: Array<() => void> = [];

    constructor(network: Network, actionFactory: ActionFactory, 
                timedTriggerHandler: TimedTriggerHandler = new TimeoutTimedTriggerHandler()) {
        const initialState = network.states.get(network.initial);

        if (initialState === undefined) {
            throw new ErrorMissingInitialState(network.initial);
        }
        this.network = network;
        this.actionFactory = actionFactory;
        this.timedTriggerHandler = timedTriggerHandler;
        this.initialiseRegisters(network.registers);
        this.enterState(network.initial);
    }
    
    private initialiseRegisters(registers: Map<string, Register>) {
        this.currentRegisters.clear();
        registers.forEach((register, key) => {
            this.currentRegisters.set(key, new Register(register.serialise()));
        });
    }

    private enterState(stateKey: string) {
        if (this.controller !== undefined) {
            this.controller.handleEnterState(stateKey, this);
            this.currentlyPaused = this.currentlyPaused || this.controller.getPaused(stateKey);
        }
        if (this.setState(stateKey)) {
            this.timedTriggerHandler.clear();
            if (this.currentState.child) {
                this.enterState(this.currentState.child);
            } else {
                this.pendingActions = true;
                if (this.currentlyPaused) return;
                this.executeActions();
                if (this.currentlyStepping) {
                    this.currentlyStepping = false;
                    this.currentlyPaused = true;
                }
            }
        }
        this.updateDelegates.forEach(delegate => delegate());
    };
    private executeActions() {
        this.currentState.actions.every(marshal => {
            const action = this.actionFactory.get(marshal.action);

            this.controller?.handleExecuteAction(marshal, this);
            if (action) {
                try {
                    return ! action.Resolve(this, marshal.parameters || {}) || ! action.terminating();
                }
                catch (exception: any) {
                    console.error(`Threw exception executing ${marshal.action} in state ${this.currentStateKey}`);
                    console.error(exception.message);
                    return false;
                }
            }
            return true;
        });
        this.pendingActions = this.currentlyPaused;
    }
    private setState(stateKey: string): boolean {
        const state = this.network.states.get(stateKey);

        if (state) {
            this.currentStateKey = stateKey;
            this.currentState = state;
            return true;
        }
        return false;
    };

    public get actions() {
        return this.currentState.actions;
    }
    public get name() {
        return this.currentState.name;
    }
    public get paused() {
        return this.currentlyPaused;
    }
    public get state() {
        return this.currentStateKey;
    }
    public get stepping() {
        return this.currentlyStepping;
    }
    public get registers() {
        return this.currentRegisters;
    }
    public addUpdateDelegate(delegate: () => void) {
        if (! this.updateDelegates.includes(delegate)) {
            this.updateDelegates.push(delegate);
        }
    }
    public removeUpdateDelegate(delegate: () => void) {
        const index = this.updateDelegates.indexOf(delegate);

        if (index >= 0) {
            this.updateDelegates.slice(index, 1);
        }
    }
    public setDebugController(controller: IDebugController | undefined) {
        this.controller = controller;
    }
    public pause() {
        this.currentlyPaused = true;
    }
    public play() {
        this.currentlyPaused = false;
        this.currentlyStepping = false;
        if (this.pendingActions) {
            this.executeActions();
        }
    }
    public step() {
        this.currentlyStepping = true;
        this.currentlyPaused = false;
    }
    public reload(network: Network) {
        const currentState = network.states.get(this.currentStateKey);

        // These could refer to old states and have to go
        this.timedTriggerHandler.clear();
        if (currentState !== undefined) {
            this.currentState = currentState;
            this.network = network;
            // Add any new registers
            this.network.registers.forEach((register, key) => {
                if (this.currentRegisters.get(key) === undefined) {
                    this.currentRegisters.set(key, new Register(register.serialise()));
                }
            });
            // Remove any deleted registers
            this.registers.forEach((register, key) => {
                if (network.registers.get(key) === undefined) {
                    this.registers.delete(key);
                }
            });
        }
    }
    public get triggers(): string[] {
        return (this.currentState.transitions)
            .map(transition => transition.trigger || "");
    }
    public timedTrigger(delay: number, trigger: string) {
        this.timedTriggerHandler.push(delay, () => {
            this.timedTriggerHandler.clear();
            this.trigger(trigger);
        });
    }
    public trigger(trigger: string) {
        if (this.currentState) {
            const transition = this.currentState.getTransition(trigger);

            if (transition && transition.destination) {
                if (this.currentState.parent === transition.destination) {
                    // Pop network
                    this.setState(transition.destination);
                } else {
                    this.enterState(transition.destination);
                }
            }
        }
    }
    public reset() {
        this.timedTriggerHandler.clear();
        this.network.registers.forEach((value, key) => {
            this.currentRegisters.get(key)?.set(value.get());
        })
        this.enterState(this.network.initial);
    } 
    public forceState(stateKey: string, registers?: Map<string, Register>) {
        if (registers) {
            registers.forEach((value, key) => this.currentRegisters.set(key, value));
        }
        this.enterState(stateKey);
    }
    public getRegister(name: string): Register {
        const register = this.currentRegisters.get(name);

        if (register !== undefined)
            return register;
        throw new ErrorRegisterNotFound(name);
    }
    public getRegisterValue(name: string): RegisterValue {
        const register = this.currentRegisters.get(name);

        if (register !== undefined)
            return register.get();
        throw new ErrorRegisterNotFound(name);
    }
    public hasRegister(name: string): boolean {
        return this.currentRegisters.has(name);
    }
    public loadGame(save: string) : boolean {
        const saveGame = JSON.parse(save);

        if (! this.network.states.has(saveGame.state) ||
            Object.keys(saveGame.registers).some(key => ! this.registers.has(key))) {
            return false;
        }
        Object.keys(saveGame.registers).forEach(key => {
            this.registers.set(key, new Register(saveGame.registers[key]));
        });
        this.setState(saveGame.state);
        return true;
    }
    public saveGame() : string {
        const registers: {[key: string]: RegisterData} = {};

        this.registers.forEach((register, key) =>
            registers[key] = register.serialise());
        return JSON.stringify({state: this.currentStateKey, registers});
    }
    public setRegister(name: string, value: RegisterValue) {
        const register = this.currentRegisters.get(name);

        if (register !== undefined) {
            register.set(value);
        } else {
            throw new ErrorRegisterNotFound(name);
        }
    }
    public testExpression(content: string): boolean {
        return testExpression(content, this);
    }
    public triggerEnabled(trigger: string) {
        const transition = this.currentState?.getTransition(trigger);

        if (transition && transition.destination) {
            const destination = this.network.states.get(transition.destination);

            return destination && destination.transitions.length > 0;
        }
        return false;
    }
    public updateNetwork(network: NetworkData) {
        this.network = new Network(network);
        if (network.states[this.currentStateKey] !== undefined) {
            // Can change network without impacting current state, though we will have to
            const newRegisterKeys = Object.keys(network.registers || {});

            newRegisterKeys.forEach(key => {
                const register = this.registers.get(key);

                if (register === undefined || register.type !== network.registers![key].type) {
                    this.registers.set(key, new Register(network.registers![key]));
                }
            });
            Array.from(this.registers.keys()).forEach(key => {
                if (! newRegisterKeys.includes(key)) {
                    this.registers.delete(key);
                }
            });
            this.setState(this.currentStateKey);
        } else {
            // Cannot replace silently, current state deleted, reinitialise cursor
            this.initialiseRegisters(this.network.registers);
            this.enterState(network.initial);
        }
    }
}